'use client'

import Image from 'next/image'
import { Mail, Building2, Phone, Video, type LucideIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { Eyebrow } from '@/components/ui/eyebrow'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'
import { CALENDLY_URL, openCalendly } from '@/components/ui/calendly-button'

const contactIcons: LucideIcon[] = [Mail, Phone, Video, Building2]
const contactHrefs = [
  'mailto:office@infiteq.io',
  'tel:+381649543183',
  CALENDLY_URL,
  undefined,
]
const contactIconBg = [
  'rgba(43,90,255,0.12)',
  'rgba(100,60,255,0.12)',
  'rgba(140,35,230,0.12)',
  'rgba(160,32,200,0.12)',
]
const contactIconColor = ['#2B5AFF', '#643CFF', '#8C23E6', '#A020C8']

export function Contact() {
  const t = useTranslations('agency.contact')
  const items = t.raw('items') as Array<{
    title: string
    description: string
    linkLabel: string
  }>

  return (
    <Section id="contact" tone="white" spacing="lg">
      <div className="mx-auto w-full max-w-container px-6 md:px-12">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:gap-16">
          {/* Left: text content */}
          <div className="w-full flex-1">
            <Eyebrow>{t('eyebrow')}</Eyebrow>
            <Heading as="h2" size="h2" className="mt-4">
              {t('title')}
            </Heading>
            <Text className="mt-4" tone="muted">
              {t('subtitle')}
            </Text>

            <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
              {items.map(({ title, description, linkLabel }, i) => {
                const Icon = contactIcons[i]
                const href = contactHrefs[i]
                const iconBg = contactIconBg[i]
                const iconColor = contactIconColor[i]
                const isLink = href !== undefined

                return (
                  <div key={i}>
                    <div
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full"
                      style={{ backgroundColor: iconBg, color: iconColor }}
                    >
                      <Icon size={20} />
                    </div>
                    <Text
                      as="span"
                      size="lg"
                      weight="semibold"
                      className="mt-2 block text-text-primary"
                    >
                      {title}
                    </Text>
                    <Text size="base" tone="muted" className="mb-4 mt-1">
                      {description}
                    </Text>
                    {isLink && href ? (
                      href.includes('calendly') ? (
                        <button
                          type="button"
                          onClick={openCalendly}
                          className="font-semibold hover:underline"
                          style={{ color: iconColor }}
                        >
                          {linkLabel}
                        </button>
                      ) : (
                        <a
                          href={href}
                          {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener' } : {})}
                          className="font-semibold hover:underline"
                          style={{ color: iconColor }}
                        >
                          {linkLabel}
                        </a>
                      )
                    ) : (
                      <span className="font-semibold" style={{ color: iconColor }}>{linkLabel}</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right: map */}
          <div className="hidden w-full lg:w-[480px] lg:shrink-0">
            <Image
              src="/map.png"
              alt={t('mapAlt')}
              width={480}
              height={400}
              className="w-full rounded-2xl object-cover"
            />
          </div>
        </div>
      </div>
    </Section>
  )
}
